<template>
    <div class="price-plan-summary">
      <div class='row'>
        <div class='small-12 medium-12 large-12 hd-12 columns dashboard-content'>
          <dean-section-title content='true'>
            <span slot='title'>
                <span class='avenir-heavy'>Prijsplan</span>
            </span>
            <dean-section-title-item
                :icon='plusIcon'
                :content="true"
                @click.native="addPricePlanEntry"
            >
              toevoegen
            </dean-section-title-item>
          </dean-section-title>
          <dean-table
              :data='pricePlanEntries'
              :columns="columns"
              :hover="true"
              @column-click='columnClick'
              @cell-click="cellClick">
          </dean-table>
          <dean-pagination
              :current-page='metaData.pageNumber'
              :total-items='metaData.totalResults'
              :items-per-page='metaData.itemsPerPage'
              :select-items-per-page='selectItemsPerPage'
              :visible-pages='metaData.visiblePages'
              @page-changed='paginationChanged'
              @items-per-page='itemsPerPageChanged'
          >
          </dean-pagination>
        </div>
      </div>
    </div>
</template>

<script lang="ts">
  import {Component} from 'vue-property-decorator';
  import deanTableMixin from '@/mixins/deanTableMixin';
  import {mixins} from 'vue-class-component';
  import {getDutchDateFormat} from '@/mixins/dateWrapper';

  import {
    // ICONS
    plusIcon,
    editIcon,
    // COMPONENTS
    deanSectionTitle,
    deanSectionTitleItem,
    deanTable,
    deanPagination,
  } from '@deanone/dean-components';
  import vSelect from 'vue-select';
  import Multiselect from 'vue-multiselect';
  import {deanDialogBus} from '@/main';
  import { formatPrice } from '@/mixins/formatPrice';

  @Component({
    components: {
      deanSectionTitle,
      deanSectionTitleItem,
      deanTable,
      deanPagination,
      vSelect,
      Multiselect,
    },
  })
  export default class List extends mixins(deanTableMixin) {
    // DATA
    public pricePlanEntries: any = [];
    public pricePlanName: string = '';
    public plusIcon: object = plusIcon;
    public periods: any = [];
    public columns: any = {
      productName: {
        content: 'Product',
        align: 'left',
        sort: true,
        dql: 'productName',
      },
      productCode: {
        content: 'Productcode',
        align: 'left',
        sort: true,
        dql: 'productCode',
      },
      connectPrice: {
        content: 'Aansluitkosten',
        align: 'left',
        sort: true,
        dql: 'connectPrice',
      },
      recurringPrice: {
        content: 'Maandelijkse kosten',
        align: 'left',
        sort: true,
        dql: 'recurringPrice',
      },
      disconnectPrice: {
        content: 'Afsluitkosten',
        align: 'left',
        sort: true,
        dql: 'disconnectPrice',
      },
      startDate: {
        content: 'Startdatum',
        align: 'center',
        sort: true,
        dql: 'startDate',
      },
      endDate: {
        content: 'Einddatum',
        align: 'center',
        sort: true,
        dql: 'endDate',
      },
    };

    // COMPUTED
    get metaData(): object {
      return this.pageMeta;
    }

    get sideMenu(): object {
      return {
        content: {
          title: {
            text: 'Prijsplan ' + this.pricePlanName,
          },
          type: 'themed',
          sections: {
            general: {
              items: {
                settings: {
                  title: 'Instellingen',
                  name: 'standingData.pricePlans.edit',
                },
                back: {
                  title: 'Terug naar Prijsplannen',
                  name: 'standingData.pricePlans',
                },
              },
            },
            periods: {
              title: 'Periode',
              items: this.periods,
            },
            options: {
              title: '',
              items: {
                edit: {
                  title: 'Periode wijzigen',
                  name: 'PricePlanPeriodDialog',
                  icon: editIcon,
                  disabled: (!this.$route.params.startDate || !this.$route.params.endDate),
                  params: {
                    modal: true,
                    view: 'period',
                    context: 'priceplan',
                    contextId: this.$route.params.ppId,
                    existing: 1,
                    action: 'edit',
                    oldStartDate: this.$route.params.startDate,
                    oldEndDate: this.$route.params.endDate,
                  },
                },
                add: {
                  title: 'Periode toevoegen',
                  name: 'PricePlanPeriodDialog',
                  icon: plusIcon,
                  params: {
                    modal: true,
                    view: 'period',
                    context: 'priceplan',
                    contextId: this.$route.params.ppId,
                    action: 'add',
                  },
                },
              },
            },
          },
        },
      };
    }

    public tableName(): string {
      return 'settings-pricePlan';
    }

    // METHODS
    public cellClick(target: {
      cell: object, row: { id: string, startDate: string, endDate: string }, column: string,
    }) {
      this.$router.push({
        name: 'standingData.pricePlan.entries.edit',
        params: {
          id: target.row.id,
          startDate: target.row.startDate,
          endDate: target.row.endDate,
        },
      });
    }

    public addPricePlanEntry() {
      if (this.$route.params.startDate && this.$route.params.endDate) {
        this.sectionRoute('standingData.pricePlan.entries.add');
      } else {
        deanDialogBus.$emit('open', 'PricePlanPeriodDialog', {
          context: 'priceplan',
          contextId: this.$route.params.ppId,
          view: 'entry',
        });
      }
    }

    public loadList(data: any): void {
      this.$store.dispatch('setLoadOverlay', true);

      this.$store.dispatch('requestPricePlan', {
        id: this.$route.params.ppId,
      })
      .then(
        (response: any) => {
          const pricePlan = response.data;

          this.pricePlanName = pricePlan.name;

          return this.$store.dispatch('requestPricePlanEntries', {
            pricePlanId: this.$route.params.ppId,
            startDate: this.$route.params.startDate,
            endDate: this.$route.params.endDate,
            sort: data.sort,
            pageNumber: data.pageNumber,
            itemsPerPage: data.itemsPerPage,
          });
        },
        (error: any) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      )
      .then(
        (response) => {
          const pricePlanEntries = response.data.data;

          this.pricePlanEntries = pricePlanEntries.map((pricePlanEntry: any) => {
            return {
              id: pricePlanEntry.id,
              productName: pricePlanEntry.product ? pricePlanEntry.product.privateName : '',
              productCode: pricePlanEntry.product ? pricePlanEntry.product.code : '',
              connectPrice: formatPrice(pricePlanEntry.connectPrice),
              recurringPrice: formatPrice(pricePlanEntry.recurringPrice),
              disconnectPrice: formatPrice(pricePlanEntry.disconnectPrice),
              startDate: getDutchDateFormat(pricePlanEntry.startDate),
              endDate: getDutchDateFormat(pricePlanEntry.endDate),
            };
          });

          this.updateMeta(response.data.meta);

          return this.$store.dispatch('requestPricePlanPeriods', {
            pricePlanId: this.$route.params.ppId,
          });
        },
        (error) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      )
      .then(
        (response: any) => {
          const pricePlanPeriods = response.data;

          this.updatePeriodsForSideMenu(pricePlanPeriods);
        },
        (error: any) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      )
      .finally(() => {
        this.$store.dispatch('updateSideMenu', this.sideMenu);
        this.$store.dispatch('setLoadOverlay', false);
      });
    }

    public created() {
      this.setDefaultSort('productName');
      this.loadList(this.pageMeta);
    }

    private updatePeriodsForSideMenu(pricePlanPeriods: any): void {
      this.periods = [];

      const periodList = this.periods;

      pricePlanPeriods.forEach((pricePlanPeriod: any) => {
        const state = (
          this.$route.params.startDate === pricePlanPeriod.startDate &&
          this.$route.params.endDate === pricePlanPeriod.endDate
        ) ? 'active' : '';

        const period = {
          title: pricePlanPeriod.startDate + ' - ' + pricePlanPeriod.endDate,
          state,
          name: 'standingData.pricePlan.entries.filter',
          params: {
            startDate: pricePlanPeriod.startDate,
            endDate: pricePlanPeriod.endDate,
          },
        };

        periodList.push(period);
      });

      if (
        this.$route.params.startDate &&
        this.$route.params.endDate &&
        ! periodList.some((pricePlanPeriod: any) => (
          pricePlanPeriod.params.startDate === this.$route.params.startDate &&
          pricePlanPeriod.params.endDate === this.$route.params.endDate
        ))
      ) {
        const period = {
          title: this.$route.params.startDate + ' - ' + this.$route.params.endDate,
          state: 'active',
          name: 'standingData.pricePlan.entries.filter',
          params: {
            startDate: this.$route.params.startDate,
            endDate: this.$route.params.endDate,
          },
        };

        periodList.unshift(period);
      }

      this.periods = periodList;
    }
  }
</script>

<style lang="scss" scoped>
  // IMPORT
  @import '~@deanone/dean-components/src/scss/vars';
</style>
